import { useCallback, useMemo } from 'react';
import { Offer, ShortOfferDto, useAddOfferToPlan, useOffer } from '@app/api';
import { useTranslation } from 'react-i18next';
import { Theme } from '@app/theme';
import { Button, CenteredModal, useToast } from '@app/core';
import { createUseStyles } from 'react-jss';
import { usePlanId } from './PlanIdContext';
import { getSimpleOfferPrice, PriceDisplayMethod } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  overlay: {
    zIndex: 10000,
  },
  invoiceBox: {
    marginBottom: theme.marginBase * 2,
  },
  rightButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  offer: Offer | ShortOfferDto;
}

export const UpsellOptionModal = ({ isOpen, onRequestClose, offer }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const classes = useStyles();

  const { data: option } = useOffer(offer.id);
  const planId = usePlanId();
  const { mutateAsync: addOption } = useAddOfferToPlan(planId);

  const { amount, priceDisplayMethod } = useMemo(
    () => getSimpleOfferPrice(option?.products),
    [option],
  );

  const unlock = useCallback(async () => {
    try {
      await addOption({ offerId: offer.id });
      toast.saved();
      onRequestClose();
    } catch (e) {
      toast.exception(e);
    }
  }, [toast, addOption, onRequestClose, offer]);

  return (
    <CenteredModal
      title={t('editSession.upsellOption.title', {
        name: offer.name || option?.name,
      })}
      text={option?.description}
      isOpen={isOpen && !!option}
      onRequestClose={onRequestClose}
      overlayClassName={classes.overlay}
      buttons={[
        <Button
          onClick={onRequestClose}
          text={t('editSession.upsellOption.cancel')}
          key="cancel"
          type="button"
          color="red"
          variant="transparent"
        />,
        <Button
          text={t('editSession.upsellOption.confirm', {
            price: amount,
            context:
              priceDisplayMethod === PriceDisplayMethod.FIXED
                ? 'fixed'
                : undefined,
            name: offer.name || option?.name,
          })}
          onClick={unlock}
          key="confirm"
          type="button"
          variant="transparent"
        />,
      ]}
    />
  );
};
